import { Link, Meta, Title } from '@solidjs/meta';
import { useLocation } from '@solidjs/router';
import { useVenue } from 'Shared/VenueProvider';
import { title } from 'Shared/frontend/Title';
import { useSite } from 'Shared/SiteProvider';
import { useUrls } from 'Shared/UrlProvider';
import { createMemo, Show } from 'solid-js';
import { VenueFontLinks } from 'Shared/frontend/fonts/VenueFontLinks';
import { VenueDoc } from 'Shared/model/venue';
import { Urls } from 'Common/Urls';
import { displayState } from 'Shared/frontend/displayState';


export function PageMeta(P:{title?:string})
{
	const venue = useVenue();

	const photo = venue.photo;
	const imageUrl = createMemo(() => {
		if (!photo) return undefined;

		return {
			...photo.formats.medium,
			url: venuePhotoUrl(useUrls(),venue)
		};
	});

	const site = useSite();
	const url = canonicalUrl(site.hostUrl,useLocation().pathname);
	const tit = () => title(venue,P.title);

	return (<>
		<Title>{tit()}</Title>
		<Link rel='canonical' href={url} />

		<Meta property='og:type' content='website' />
		<Meta property='og:url' content={url} />
		<Meta property='og:site_name' content={venue.nickname ?? venue.name} />
		<Meta property='og:title' content={tit()} />

		<Show when={imageUrl()}>
			<OgImage {...imageUrl()} />
		</Show>

		<GoogleInfo data={{...venuePlace(venue), url: site.hostUrl, image: imageUrl()?.url}} />

		<VenueFontLinks fonts={venue.settings.frontend.fonts} />
	</>);
}


//FIXME or use Image or ImageDetails or a new type? SEE: StandardHeadInsert. May be a bug... cf format vs formats
export function OgImage(P:{url?:string,format?:string,width?:number,height?:number})
{
	return (<>
		<Meta property='og:image' content={P.url} />

		<Show when={P.format}>
			<Meta property='og:image:type' content={P.format} />
		</Show>
		<Show when={P.width}>
			<Meta property='og:image:width' content={String(P.width)} />
		</Show>
		<Show when={P.height}>
			<Meta property='og:image:height' content={String(P.height)} />
		</Show>
	</>);
}


export function GoogleInfo(P:{data:object})
{
	/* This will find its way into the body. According to Google this is OK. */
	return (
		<script type='application/ld+json'>
			{JSON.stringify({ '@context': 'https://schema.org', ...P.data })}
		</script>
	);
}

/* The server redirects '/x/' to '/x', but the www. redirect's client-side landing can still show the slash */
export function canonicalUrl(hostUrl:string,pathname:string)
{
	return hostUrl + (pathname.replace(/\/+$/,'') || '/');
}

export function venuePlace(venue:VenueDoc)
{
	return {
		'@type': 'Place',
		name: venue.name,
		address: {
			'@type': 'PostalAddress',
			streetAddress: venue.streetAddress,
			addressLocality: venue.suburb,
			postalCode: venue.postcode,
			addressRegion: displayState(venue.state) ?? venue.state,
			addressCountry: countryCodes[venue.country ?? '']
		}
	};
}

export function venuePhotoUrl(urls:Urls,venue:VenueDoc)
{
	const photo = venue.photo;
	if (!photo) return undefined;
	return urls.absoluteImageUrl('Venue-photo',false,photo.hash,'medium','jpeg',photo.dev ?? false,true);
}

const countryCodes:Record<string,string> = {aus:'AU',usa:'US',uk:'GB'};
